import React, { useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { getApiBaseUrl } from '../utils/apiConfig'

const API_BASE_URL = getApiBaseUrl()

const MONTHS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' }
]

const YEARS = Array.from({ length: 7 }, (_, i) => new Date().getFullYear() - 4 + i)

export default function BankStatement() {
  const { user } = useAuth()
  const { isDark } = useTheme()
  const [uploading, setUploading] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [importing, setImporting] = useState(false)
  const [uploadedFile, setUploadedFile] = useState(null)
  const [extractedData, setExtractedData] = useState(null)
  const [error, setError] = useState(null)
  const [progress, setProgress] = useState(0)
  const [successMessage, setSuccessMessage] = useState(null)
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [editableTransactions, setEditableTransactions] = useState([])
  const [statementMonth, setStatementMonth] = useState('')
  const [statementYear, setStatementYear] = useState('')

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg']
    if (!validTypes.includes(file.type)) {
      setError('Please upload a PDF or image file (JPG, PNG)')
      return
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB')
      return
    }

    setError(null)
    setUploadedFile(file)
    setExtractedData(null)
  }

  const processBankStatement = async () => {
    if (!uploadedFile) return

    if (!statementMonth || !statementYear) {
      setError('Please select the statement Month and Year')
      return
    }

    try {
      setUploading(true)
      setProcessing(true)
      setError(null)
      setProgress(0)

      // Rename file according to selected month/year: bank-statement-YYYY-MM.ext
      const ext = uploadedFile.name.substring(uploadedFile.name.lastIndexOf('.')) || ''
      const renamedFile = new File([uploadedFile], `bank-statement-${statementYear}-${statementMonth}${ext}`, {
        type: uploadedFile.type
      })

      const formData = new FormData()
      formData.append('file', renamedFile)
      formData.append('uploaded_by', user?.user_id || 1)
      formData.append('statement_month', statementMonth)
      formData.append('statement_year', statementYear)

      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 20, 90))
      }, 500)

      // Upload and process with OCR
      const response = await fetch(`${API_BASE_URL}/banking/bank-statements/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      })

      clearInterval(progressInterval)
      setProgress(100)

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Failed to process bank statement')
      }

      setExtractedData(data.data.extracted_data || data.data)
      setProcessing(false)

      // Show success message
      if (data.data?.extracted_data?.transactions?.length > 0 || data.data?.transactions?.length > 0) {
        setError(null)
      }
    } catch (err) {
      console.error('Error processing bank statement:', err)
      setError(err.message || 'Failed to process bank statement')
      setProcessing(false)
    } finally {
      setUploading(false)
    }
  }

  const handleImportAllTransactions = async () => {
    if (!extractedData?.transactions || extractedData.transactions.length === 0) {
      setError('No transactions to import')
      return
    }

    try {
      setImporting(true)
      setError(null)
      setSuccessMessage(null)

      // Import transactions to the database
      const importPromises = extractedData.transactions.map(async (transaction) => {
        try {
          const response = await fetch(`${API_BASE_URL}/banking/transactions`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              transaction_date: transaction.date,
              transaction_type: 'receipt', // Default type for imported transactions
              amount: parseFloat(transaction.amount),
              cash_account_id: 1, // Default cash account - you might want to make this configurable
              contra_account_id: 2, // Default contra account
              description: transaction.description,
              reference: `IMPORT-${transaction.date}-${transaction.description.substring(0, 10).replace(/\s/g, '')}`
            })
          })

          if (!response.ok) {
            const errorData = await response.json()
            console.error('Import error for transaction:', errorData)
            return { success: false, transaction, error: errorData.message }
          }

          return { success: true, transaction }
        } catch (error) {
          console.error('Network error for transaction:', error)
          return { success: false, transaction, error: error.message }
        }
      })

      const results = await Promise.all(importPromises)

      const successCount = results.filter(r => r.success).length
      const failCount = results.filter(r => !r.success).length

      if (successCount > 0) {
        setSuccessMessage(`Successfully imported ${successCount} transactions!${failCount > 0 ? ` (${failCount} failed)` : ''}`)

        // Show detailed results
        if (failCount > 0) {
          setError(`${failCount} transactions failed to import.`)
        }

        // Clear extracted data and statement month/year after successful import
        // (forces a fresh selection so only one statement per month can be uploaded)
        setTimeout(() => {
          setExtractedData(null)
          setUploadedFile(null)
          setStatementMonth('')
          setStatementYear('')
        }, 3000)
      } else {
        setError('Failed to import any transactions. Please try again.')
      }

    } catch (err) {
      console.error('Error importing transactions:', err)
      setError(err.message || 'Failed to import transactions')
    } finally {
      setImporting(false)
    }
  }

  const handleReviewAndEdit = () => {
    if (!extractedData?.transactions || extractedData.transactions.length === 0) {
      setError('No transactions to review')
      return
    }

    // Create editable copy of transactions
    setEditableTransactions(
      extractedData.transactions.map(txn => ({
        ...txn,
        editable: true,
        selected: false
      }))
    )
    setShowReviewModal(true)
    setError(null)
  }

  const handleTransactionChange = (index, field, value) => {
    const updated = [...editableTransactions]
    updated[index] = {
      ...updated[index],
      [field]: value
    }
    setEditableTransactions(updated)
  }

  const handleTransactionSelect = (index, selected) => {
    const updated = [...editableTransactions]
    updated[index] = {
      ...updated[index],
      selected
    }
    setEditableTransactions(updated)
  }

  const handleSelectAllTransactions = () => {
    const allSelected = editableTransactions.every(t => t.selected)
    const updated = editableTransactions.map(t => ({
      ...t,
      selected: !allSelected
    }))
    setEditableTransactions(updated)
  }

  const handleDeleteTransaction = (index) => {
    const updated = editableTransactions.filter((_, i) => i !== index)
    setEditableTransactions(updated)

    if (updated.length === 0) {
      setShowReviewModal(false)
      setError('All transactions deleted')
    }
  }

  const handleDeleteSelectedTransactions = () => {
    const updated = editableTransactions.filter(t => !t.selected)
    setEditableTransactions(updated)

    if (updated.length === 0) {
      setShowReviewModal(false)
      setError('All selected transactions deleted')
    } else {
      setSuccessMessage(`Deleted ${editableTransactions.length - updated.length} transactions`)
      setTimeout(() => setSuccessMessage(null), 2000)
    }
  }

  const handleImportSelectedTransactions = async () => {
    if (editableTransactions.length === 0) {
      setError('No transactions to import')
      return
    }

    try {
      setImporting(true)
      setError(null)
      setSuccessMessage(null)

      // Import transactions to the database
      const importPromises = editableTransactions.map(async (transaction) => {
        try {
          const response = await fetch(`${API_BASE_URL}/banking/transactions`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              transaction_date: transaction.date,
              transaction_type: 'receipt',
              amount: parseFloat(transaction.amount),
              cash_account_id: 1,
              contra_account_id: 2,
              description: transaction.description,
              reference: `IMPORT-${transaction.date}-${transaction.description.substring(0, 10).replace(/\s/g, '')}`
            })
          })

          if (!response.ok) {
            const errorData = await response.json()
            console.error('Import error for transaction:', errorData)
            return { success: false, transaction, error: errorData.message }
          }

          return { success: true, transaction }
        } catch (error) {
          console.error('Network error for transaction:', error)
          return { success: false, transaction, error: error.message }
        }
      })

      const results = await Promise.all(importPromises)

      const successCount = results.filter(r => r.success).length
      const failCount = results.filter(r => !r.success).length

      if (successCount > 0) {
        setSuccessMessage(`Successfully imported ${successCount} transactions!${failCount > 0 ? ` (${failCount} failed)` : ''}`)

        // Update extracted data with remaining transactions
        const failedTransactions = results.filter(r => !r.success).map(r => r.transaction)
        setExtractedData({
          ...extractedData,
          transactions: failedTransactions
        })
        setEditableTransactions(failedTransactions)

        if (failCount > 0) {
          setError(`${failCount} transactions failed to import.`)
        }

        // Close modal if all transactions were imported successfully
        if (failCount === 0) {
          setTimeout(() => {
            setShowReviewModal(false)
            setExtractedData(null)
            setUploadedFile(null)
            setStatementMonth('')
            setStatementYear('')
          }, 2000)
        }
      } else {
        setError('Failed to import any transactions. Please try again.')
      }

    } catch (err) {
      console.error('Error importing transactions:', err)
      setError(err.message || 'Failed to import transactions')
    } finally {
      setImporting(false)
    }
  }

  const bgColor = isDark ? 'bg-slate-900' : 'bg-white'
  const textColor = isDark ? 'text-white' : 'text-gray-800'
  const borderColor = isDark ? 'border-slate-700' : 'border-gray-200'
  const cardBgColor = isDark ? 'bg-slate-800' : 'bg-gray-50'
  const inputBgColor = isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-700'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className={`text-2xl font-bold ${textColor}`}>📄 Bank Statement Upload</h2>
        <p className={`mt-1 text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
          Upload your bank statement and let AI extract transaction data automatically
        </p>
      </div>

      {/* Upload Section */}
      <div className={`${cardBgColor} rounded-lg p-6 border ${borderColor}`}>
        <div className="flex items-start gap-6">
          <div className="flex-1">
            <h3 className={`text-lg font-semibold ${textColor} mb-2`}>Upload Bank Statement</h3>
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'} mb-4`}>
              Supported formats: PDF, JPG, PNG (Max 10MB)
            </p>

            <div className="flex items-center gap-4 flex-wrap">
              <input
                type="file"
                onChange={handleFileUpload}
                accept=".pdf,.jpg,.jpeg,.png"
                disabled={uploading}
                className={`flex-1 min-w-[220px] px-4 py-2 rounded-lg border ${inputBgColor} focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50`}
              />

              <div className="flex items-center gap-2">
                <select
                  value={statementMonth}
                  onChange={(e) => setStatementMonth(e.target.value)}
                  disabled={uploading || processing}
                  className={`px-3 py-2 rounded-lg border ${inputBgColor} focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50`}
                >
                  <option value="">Month</option>
                  {MONTHS.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                <select
                  value={statementYear}
                  onChange={(e) => setStatementYear(e.target.value)}
                  disabled={uploading || processing}
                  className={`px-3 py-2 rounded-lg border ${inputBgColor} focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50`}
                >
                  <option value="">Year</option>
                  {YEARS.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              {uploadedFile && (
                <button
                  onClick={processBankStatement}
                  disabled={uploading || processing || !statementMonth || !statementYear}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? 'Uploading...' : processing ? 'Processing...' : 'Process with AI OCR'}
                </button>
              )}
            </div>

            {uploadedFile && (
              <div className={`mt-4 p-3 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-white'} border ${borderColor}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">📄</span>
                    <div>
                      <p className={`text-sm font-medium ${textColor}`}>{uploadedFile.name}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                        {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                        {statementMonth && statementYear && (
                          <> · will be saved as <span className="font-medium">bank-statement-{statementYear}-{statementMonth}{uploadedFile.name.substring(uploadedFile.name.lastIndexOf('.'))}</span></>
                        )}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setUploadedFile(null)
                      setExtractedData(null)
                      setError(null)
                    }}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div className={`mt-4 p-3 rounded-lg bg-red-50 border border-red-200`}>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {successMessage && (
              <div className={`mt-4 p-3 rounded-lg bg-green-50 border border-green-200`}>
                <p className="text-sm text-green-600">{successMessage}</p>
              </div>
            )}

            {(uploading || processing) && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-medium ${textColor}`}>
                    {processing ? 'Processing with AI OCR...' : 'Uploading file...'}
                  </span>
                  <span className={`text-sm ${textColor}`}>{progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="hidden md:block">
            <div className="text-6xl">🤖</div>
          </div>
        </div>
      </div>

      {/* Extracted Data Preview */}
      {extractedData && (
        <div className={`${cardBgColor} rounded-lg p-6 border ${borderColor}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-semibold ${textColor}`}>Extracted Transaction Data</h3>
            <div className="flex gap-2">
              <button
                onClick={handleReviewAndEdit}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition"
              >
                ✓ Import All Transactions
              </button>
              <button
                onClick={handleReviewAndEdit}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition"
              >
                Review & Edit
              </button>
            </div>
          </div>

          <div className={`rounded-lg border ${borderColor} overflow-hidden`}>
            <table className="w-full">
              <thead className={isDark ? 'bg-slate-700' : 'bg-gray-100'}>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Description</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider">Amount</th>
                  <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider">Confidence</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-200'}`}>
                {extractedData.transactions && extractedData.transactions.length > 0 ? (
                  extractedData.transactions.slice(0, 5).map((transaction, index) => (
                  <tr key={index} className={isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-3 text-sm">{transaction.date}</td>
                    <td className="px-4 py-3 text-sm">{transaction.description}</td>
                    <td className={`px-4 py-3 text-sm text-right font-medium ${
                      transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {transaction.type === 'credit' ? '+' : '-'}RM {parseFloat(transaction.amount).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        transaction.type === 'credit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {transaction.type === 'credit' ? 'Credit' : 'Debit'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <div className="flex items-center justify-center gap-1">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              transaction.confidence > 80 ? 'bg-green-500' :
                              transaction.confidence > 60 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${transaction.confidence}%` }}
                          />
                        </div>
                        <span className="text-xs">{transaction.confidence}%</span>
                      </div>
                    </td>
                  </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center">
                      <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                        No transactions found in the extracted data
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {extractedData.transactions && extractedData.transactions.length > 5 && (
            <div className={`mt-4 text-center ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
              <p className="text-sm">
                Showing 5 of {extractedData.transactions?.length || 0} extracted transactions
              </p>
              <button
                onClick={() => {
                  if (!extractedData?.transactions || extractedData.transactions.length === 0) {
                    setError('No transactions to view')
                    return
                  }

                  // Create editable copy of all transactions
                  setEditableTransactions(
                    extractedData.transactions.map(txn => ({
                      ...txn,
                      editable: true,
                      selected: false
                    }))
                  )
                  setShowReviewModal(true)
                  setError(null)
                }}
                className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                View all transactions →
              </button>
            </div>
          )}
        </div>
      )}

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={`${cardBgColor} rounded-lg p-4 border ${borderColor}`}>
          <div className="flex items-center gap-3">
            <span className="text-2xl">🎯</span>
            <div>
              <h4 className={`font-medium ${textColor}`}>High Accuracy</h4>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                AI OCR with 95%+ accuracy for bank statements
              </p>
            </div>
          </div>
        </div>

        <div className={`${cardBgColor} rounded-lg p-4 border ${borderColor}`}>
          <div className="flex items-center gap-3">
            <span className="text-2xl">⚡</span>
            <div>
              <h4 className={`font-medium ${textColor}`}>Fast Processing</h4>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                Extract hundreds of transactions in seconds
              </p>
            </div>
          </div>
        </div>

        <div className={`${cardBgColor} rounded-lg p-4 border ${borderColor}`}>
          <div className="flex items-center gap-3">
            <span className="text-2xl">🔒</span>
            <div>
              <h4 className={`font-medium ${textColor}`}>Secure & Private</h4>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                Bank-level encryption for your financial data
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Review & Edit Modal */}
      {showReviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${cardBgColor} rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden`}>
            {/* Modal Header */}
            <div className={`border-b ${borderColor} p-4`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className={`text-xl font-bold ${textColor}`}>Review & Edit Transactions</h3>
                  <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                    {editableTransactions.length} transactions ready for review
                  </p>
                </div>
                <button
                  onClick={() => setShowReviewModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
                >
                  ×
                </button>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3 mt-4">
                <button
                  onClick={handleImportSelectedTransactions}
                  disabled={importing}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {importing ? 'Importing...' : `✓ Import All (${editableTransactions.length})`}
                </button>
                <button
                  onClick={handleDeleteSelectedTransactions}
                  disabled={editableTransactions.filter(t => t.selected).length === 0}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Delete Selected ({editableTransactions.filter(t => t.selected).length})
                </button>
                <button
                  onClick={handleSelectAllTransactions}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition"
                >
                  {editableTransactions.every(t => t.selected) ? 'Deselect All' : 'Select All'}
                </button>
                <div className="flex-1"></div>
                <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                  {editableTransactions.filter(t => t.selected).length} selected
                </span>
              </div>
            </div>

            {/* Modal Body - Scrollable Table */}
            <div className="overflow-y-auto max-h-[calc(90vh-200px)]">
              <table className="w-full">
                <thead className={isDark ? 'bg-slate-700 sticky top-0' : 'bg-gray-100 sticky top-0'}>
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={editableTransactions.every(t => t.selected)}
                        onChange={(e) => handleSelectAllTransactions()}
                        className="w-4 h-4"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Description</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider">Amount</th>
                    <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider">Confidence</th>
                    <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-200'}`}>
                  {editableTransactions.map((transaction, index) => (
                    <tr
                      key={index}
                      className={isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}
                      style={{ backgroundColor: transaction.selected ? (isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)') : '' }}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={transaction.selected || false}
                          onChange={(e) => handleTransactionSelect(index, e.target.checked)}
                          className="w-4 h-4"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={transaction.date}
                          onChange={(e) => handleTransactionChange(index, 'date', e.target.value)}
                          className={`w-full px-2 py-1 rounded border ${borderColor} ${inputBgColor} text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={transaction.description}
                          onChange={(e) => handleTransactionChange(index, 'description', e.target.value)}
                          className={`w-full px-2 py-1 rounded border ${borderColor} ${inputBgColor} text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          step="0.01"
                          value={transaction.amount}
                          onChange={(e) => handleTransactionChange(index, 'amount', e.target.value)}
                          className={`w-full px-2 py-1 rounded border ${borderColor} ${inputBgColor} text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <select
                          value={transaction.type}
                          onChange={(e) => handleTransactionChange(index, 'type', e.target.value)}
                          className={`px-2 py-1 rounded border ${borderColor} ${inputBgColor} text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        >
                          <option value="credit">Credit</option>
                          <option value="debit">Debit</option>
                        </select>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${
                                transaction.confidence > 80 ? 'bg-green-500' :
                                transaction.confidence > 60 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${transaction.confidence}%` }}
                            />
                          </div>
                          <span className="text-xs">{transaction.confidence}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleDeleteTransaction(index)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Modal Footer */}
            <div className={`border-t ${borderColor} p-4 flex items-center justify-between`}>
              <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                Total: {editableTransactions.length} transactions |
                Credits: {editableTransactions.filter(t => t.type === 'credit').length} |
                Debits: {editableTransactions.filter(t => t.type === 'debit').length}
              </div>
              <button
                onClick={() => setShowReviewModal(false)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
